/**
 * Realtime Context - Smart Waste Segregation System
 *
 * Provides Supabase Realtime subscriptions for live data updates.
 * Subscribed channels: notifications (INSERT) for the current user.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const RealtimeContext = createContext();

export const SocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const channelsRef = useRef({});
  const listenersRef = useRef({ notification: [] });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      Object.values(channelsRef.current).forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current = {};
      setConnected(false);
      return;
    }

    const notifsChannel = supabase
      .channel('realtime:notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          listenersRef.current['notification'].forEach((cb) => cb(payload.new));
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setConnected(true);
      });
    channelsRef.current.notifications = notifsChannel;

    return () => {
      Object.values(channelsRef.current).forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current = {};
      setConnected(false);
    };
  }, [isAuthenticated, user]);

  const on = useCallback((event, callback) => {
    if (listenersRef.current[event]) {
      listenersRef.current[event].push(callback);
    }
  }, []);

  const off = useCallback((event, callback) => {
    if (listenersRef.current[event]) {
      listenersRef.current[event] = listenersRef.current[event].filter((cb) => cb !== callback);
    }
  }, []);

  const value = {
    socket: { on, off },
    connected,
    on,
    off,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useSocket must be used within SocketProvider');
  return context;
};

export default RealtimeContext;
